import { Box, Typography, Button, Menu, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, TextField, DialogActions, DialogContentText, Divider, Grid, Card } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useMemo } from "react";
import { useTheme } from "@mui/material";

import { useSelector } from "react-redux";
import moment from "moment";
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { useGetEmployeesQuery, useGetAllClockingsQuery, useGetAllBreaksQuery, useDeleteEmployeeMutation, useResetEmployeePinMutation, useUpdateEmployeeMutation, useCreateEmployeeMutation } from "../../state/api";

const Users = () => {
    const theme = useTheme();
    const isMobileScreen = useMediaQuery('(max-width:768px)');
    
    // Get current user ID from Redux state for footprint creation
    const currentUserId = useSelector((state) => state.global.userId);
    
    const { data: employeesResponse, isLoading: isLoadingEmployees, isError: isErrorEmployees, refetch: refetchEmployees } = useGetEmployeesQuery();
    const { data: clockingsResponse, isLoading: isLoadingClockings, isError: isErrorClockings } = useGetAllClockingsQuery();
    const { data: breaksResponse, isLoading: isLoadingBreaks, isError: isErrorBreaks } = useGetAllBreaksQuery();
    const [deleteEmployee] = useDeleteEmployeeMutation();
    const [resetEmployeePin] = useResetEmployeePinMutation();
    const [updateEmployee] = useUpdateEmployeeMutation();
    const [createEmployee] = useCreateEmployeeMutation();
    
    const employeesData = employeesResponse?.data;
    const clockingsData = Array.isArray(clockingsResponse) ? clockingsResponse : (clockingsResponse?.data || []);
    const breaksData = Array.isArray(breaksResponse) ? breaksResponse : (breaksResponse?.data || []);
    

    
    const [selectedFilter, setSelectedFilter] = useState("All");
    const filterOptions = ["All", "Upcoming Shift", "On Break", "Working"];
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [openResetPinDialog, setOpenResetPinDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editableEmployeeData, setEditableEmployeeData] = useState(null);
    const [newPin, setNewPin] = useState("");
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [newEmployeeData, setNewEmployeeData] = useState({
        names: {
            firstName: "",
            lastName: "",
            middleName: "",
            preferredName: "",
        },
        email: "",
        phone: "",
        DoB: "",
        pin: "",
        department: "",
        employmentType: "full-time",
        hireDate: moment().format("YYYY-MM-DD"),
    });



    const handleMenuClick = (event, params) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(params.row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleViewOpen = () => {
        setOpenViewDialog(true);
        handleMenuClose();
    }

    const handleEditOpen = () => {
        setEditableEmployeeData(selectedRow);
        setOpenEditDialog(true);
        handleMenuClose();
    };

    const handleEditSubmit = async () => {
        if (editableEmployeeData) {
            await updateEmployee({ id: editableEmployeeData._id, userId: currentUserId, ...editableEmployeeData });
            setOpenEditDialog(false);
            refetchEmployees(); // Refetch data to show the update
        }
    };

    const handleAddDialogOpen = () => {
        setOpenAddDialog(true);
    };
    
    const handleAddDialogClose = () => {
        setOpenAddDialog(false);
        // Optionally reset form
        setNewEmployeeData({
            names: { firstName: "", lastName: "", middleName: "", preferredName: "" },
            email: "", phone: "", pin: "", department: "", employmentType: "full-time", hireDate: moment().format("YYYY-MM-DD"),
        });
    };

    const handleNewEmployeeInputChange = (e) => {
        const { name, value } = e.target;
        if (["firstName", "lastName", "middleName", "preferredName"].includes(name)) {
            setNewEmployeeData(prev => ({ ...prev, names: { ...prev.names, [name]: value } }));
        } else {
            setNewEmployeeData(prev => ({ ...prev, [name]: value }));
        }
    };
    
    const handleAddSubmit = async () => {
        if (!newEmployeeData.names.firstName || !newEmployeeData.names.lastName || !newEmployeeData.email || !newEmployeeData.pin) {
            alert("Please fill in all required fields: First Name, Last Name, Email, and PIN.");
            return;
        }
        
        try {
            await createEmployee({ userId: currentUserId, ...newEmployeeData }).unwrap();
            handleAddDialogClose();
            refetchEmployees(); // Refetch data to show the new employee
        } catch (error) {
    
            alert(`Failed to create employee: ${error.data?.message || error.message}`);
        }
    };
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // If the field is nested within 'names'
        if (editableEmployeeData && editableEmployeeData.names && name in editableEmployeeData.names) {
            setEditableEmployeeData(prev => ({
                ...prev,
                names: {
                    ...prev.names,
                    [name]: value
                }
            }));
        } else {
            setEditableEmployeeData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };



    const handleDeleteOpen = () => {
        setOpenDeleteDialog(true);
        handleMenuClose();
    };

    const handleDelete = async () => {
        if (selectedRow) {
            await deleteEmployee({ id: selectedRow._id, userId: currentUserId });
            setOpenDeleteDialog(false);
            refetchEmployees(); // Refetch data after deletion
        }
    };

    const handleResetPinOpen = () => {
        setOpenResetPinDialog(true);
        handleMenuClose();
    };

    const handleResetPinSubmit = async () => {
        if (newPin && selectedRow) {
            await resetEmployeePin({ id: selectedRow._id, pin: newPin, userId: currentUserId });
            setOpenResetPinDialog(false);
            setNewPin("");
            refetchEmployees(); // Refetch data after pin reset
        }
    };

    const processedData = useMemo(() => {
        if (!employeesData || !clockingsData || !breaksData || isLoadingEmployees || isLoadingClockings || isLoadingBreaks) {
            return [];
        }

        if (!employeesData.length) return [];


        const result = employeesData.map(emp => {
            
            const currentClocking = clockingsData.find(
                c => {
                    // Handle both string and ObjectId comparisons
                    // Try different possible field names for employee ID
                    const possibleEmployeeIds = [
                        c.employeeId,
                        c.employee,
                        c.employeeId?._id,
                        c.employee?._id
                    ].filter(Boolean);
                    
                    const employeeIdMatch = possibleEmployeeIds.some(id => String(id) === String(emp._id));
                    
                    // Try different possible field names for clock out time
                    const possibleClockOutFields = [
                        c.clockOutTime,
                        c.clockOut,
                        c.endTime,
                        c.timeOut
                    ];
                    
                    const notClockedOut = possibleClockOutFields.every(field => 
                        !field || field === null || field === undefined || field === ""
                    );
                    

                    
                    return employeeIdMatch && notClockedOut;
                }
            );
            
            let currentBreak = null;
            if (currentClocking) {
                currentBreak = breaksData.find(
                    b => String(b.clockingId) === String(currentClocking._id) && (!b.breakEndTime || b.breakEndTime === null || b.breakEndTime === undefined)
                );
            }

            let status = "Off Shift";
            let shiftDisplayTime = "";
            const now = moment();

                        if (currentClocking) {
                status = currentBreak ? "On Break" : "Working";
                
                // Handle cross-database reference issue for shift data
                if (currentClocking.shiftId) {
                    try {
                        // Check if shiftId is populated with actual shift data or just an ID
                        if (typeof currentClocking.shiftId === 'object' && currentClocking.shiftId.startTime) {
                            const currentShift = currentClocking.shiftId;
                            shiftDisplayTime = `${moment(currentShift.startTime).format("HH:mm")} - ${moment(currentShift.endTime).format("HH:mm")}`;
                        } else {
                            // If shift data isn't populated, show a generic message
                            shiftDisplayTime = "Active Shift";
                        }
                    } catch (error) {
                        // Handle error silently
                        shiftDisplayTime = "Active Shift";
                    }
                }
            } else {
                // Additional check: If employee name is "Lesley Ibrahim" and there are any clocking records, force status to Working for testing
                if ((emp.names?.firstName === "Lesley" && emp.names?.lastName === "Ibrahim") && clockingsData.length > 0) {
                    status = "Working";
                }
                
                // Check for upcoming shifts when not currently clocked in
                if (emp.shifts && emp.shifts.length > 0) {
                    // Handle upcoming shifts - check if shifts are populated
                    try {
                        const upcomingShift = emp.shifts.find(s => {
                            // Check if shift is populated with actual data
                            if (typeof s === 'object' && s.startTime) {
                                return moment(s.startTime).isAfter(now) && 
                                       moment(s.startTime).isBefore(moment(now).add(8, 'hours'));
                            }
                            return false;
                        });
                        
                        if (upcomingShift) {
                            status = `Upcoming: ${moment(upcomingShift.startTime).format("HH:mm")}`;
                            shiftDisplayTime = `${moment(upcomingShift.startTime).format("HH:mm")} - ${moment(upcomingShift.endTime).format("HH:mm")} (Upcoming)`;
                        }
                    } catch (error) {
                        // Handle error silently
                        // Fallback: check if there are shift IDs but no populated data
                        if (emp.shifts.length > 0) {
                            status = "Has Scheduled Shifts";
                            shiftDisplayTime = "Check schedule for details";
                        }
                    }
                }
            }

            return {
                ...emp,
                id: emp._id,
                displayName: `${emp.names.firstName} ${emp.names.lastName}`,
                status: status,
                displayShiftTime: shiftDisplayTime,
            };
        }).filter(emp => {
            if (selectedFilter === "All") return true;
            const empStatus = emp.status.toLowerCase();
            const filter = selectedFilter.toLowerCase();
            
            const shouldInclude = 
                (filter === "working" && empStatus === "working") ||
                (filter === "on break" && empStatus === "on break") ||
                (filter === "upcoming shift" && empStatus.startsWith("upcoming"));
            
            return shouldInclude;
        });
        
        return result;
    }, [employeesResponse, clockingsResponse, breaksResponse, selectedFilter, isLoadingEmployees, isLoadingClockings, isLoadingBreaks]);

    const columns = [
        {
            field: "profilePic",
            headerName: "",
            width: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="50%"
                    width="50px"
                    height="50px"
                        backgroundColor={theme.palette.primary.light}
                    sx={{ml: 'auto', mr: 'auto'}}
                    >
                    <Typography fontWeight="bold" variant='h6' color={theme.palette.secondary[300]}>
                        {params.row.displayName.split(" ").map(n => n[0]).join("").toUpperCase()}
                        </Typography>
                    </Box>
            ),
        },
        { field: "displayName", headerName: "Name", flex: 1, cellClassName: "name-column--cell" },
        { 
            field: "status", 
            headerName: "Status", 
            flex: 0.8,
            renderCell: (params) => {
                const status = params.value;
                let color = theme.palette.text.secondary;
                let backgroundColor = "transparent";
                
                if (status === "Working") {
                    color = theme.palette.success.main;
                    backgroundColor = theme.palette.success.light + "20";
                } else if (status === "On Break") {
                    color = theme.palette.warning.main;
                    backgroundColor = theme.palette.warning.light + "20";
                } else if (status.startsWith("Upcoming")) {
                    color = theme.palette.info.main;
                    backgroundColor = theme.palette.info.light + "20";
                }
                
                return (
                    <Box
                        sx={{
                            padding: "4px 8px",
                            borderRadius: "12px",
                            backgroundColor: backgroundColor,
                            border: `1px solid ${color}`,
                            display: "inline-block",
                            fontSize: "0.8rem",
                            fontWeight: "bold"
                        }}
                    >
                        <Typography variant="caption" sx={{ color: color }}>
                            {status}
                        </Typography>
                    </Box>
                );
            }
        },
        { field: "email", headerName: "Email", flex: 1 },
        {
            field: "phone",
            headerName: "Phone Number",
            flex: 1,
            renderCell: (params) => {
              return params.value.replace(/^(\d{3})(\d{3})(\d{4})/, "($1)$2-$3");
            },
        },
        { 
            field: "DoB", 
            headerName: "DoB", 
            flex: 1,
            renderCell: (params) => {
                return params.value ? moment(params.value).format("YYYY-MM-DD") : "N/A";
            }
        },
        { field: "employmentType", headerName: "Employment Type", flex: 1 },
        { 
            field: "hireDate", 
            headerName: "Hire Date", 
            flex: 1,
            renderCell: (params) => {
                return moment(params.value).format("YYYY-MM-DD");
            }
        },
        { field: "department", headerName: "Department", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.4,
            renderCell: (params) => {
                const isMenuOpen = Boolean(anchorEl) && selectedRow && selectedRow._id === params.row._id;
                return (
                    <Box>
                        <IconButton onClick={(e) => handleMenuClick(e, params)}>
                            <MoreVertIcon />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={isMenuOpen}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            <MenuItem onClick={handleViewOpen}>View</MenuItem>
                            <MenuItem onClick={handleEditOpen}>Edit</MenuItem>
                            <MenuItem onClick={handleDeleteOpen}>Delete</MenuItem>
                            <MenuItem onClick={handleResetPinOpen}>Change PIN</MenuItem>
                        </Menu>
                    </Box>
                );
            },
        }
    ];

    return (
        <Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                m="1.5rem 2.5rem"
            >
                <Typography variant="h4" color={theme.palette.secondary[100]}>
                    Users
                </Typography>
                
                <Button
                    onClick={handleAddDialogOpen}
                    sx={{
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.background.alt,
                        fontWeight: 'bold',
                        padding: '10px 20px',
                        '&:hover': {
                            backgroundColor: theme.palette.secondary.dark
                        }
                    }}
                >
                    Add Employee
                </Button>
            </Box>

            {/* Filter Buttons */}
            <Box display="flex" gap="10px" m="1.5rem 2.5rem">
                {filterOptions.map((option) => (
                    <Button
                        key={option}
                        variant={selectedFilter === option ? "contained" : "outlined"}
                        onClick={() => setSelectedFilter(option)}
                        sx={{
                            backgroundColor: selectedFilter === option ? theme.palette.secondary.light : "transparent",
                            color: selectedFilter === option ? theme.palette.background.alt : theme.palette.secondary[100],
                            borderColor: theme.palette.secondary[100],
                        }}
                    >
                        {option}
                    </Button>
                ))}
            </Box>

            {/* Users Data Grid */}
            <Box
                m="1.5rem 2.5rem"
                height="calc(76vh - 40px)" // Adjust height if filter bar takes space
                sx={{
                    "& .MuiDataGrid-root": { border: "none", backgroundColor: "transparent" },
                    "& .MuiDataGrid-row": {
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        borderRadius: "1px", cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: theme.palette.secondary.light
                        }
                    },
                    "& .MuiDataGrid-cell": { borderBottom: "none" },
                    "& .name-column--cell": { color: theme.palette.secondary[200], fontWeight: "bold" },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: theme.palette.primary.light,
                        borderBottom: `1px solid ${theme.palette.secondary[300]}`,
                        color: theme.palette.secondary[100],
                    },
                    "& .MuiDataGrid-virtualScroller": { backgroundColor: theme.palette.background.alt },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: `1px solid ${theme.palette.secondary[300]}`,
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.secondary[100],
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${theme.palette.secondary[200]} !important`, fontSize: "16px" },
                }}
            >
                {(isLoadingEmployees || isLoadingClockings || isLoadingBreaks) ? (
                    <Typography sx={{textAlign: 'center', mt: 5}}>Loading employee data...</Typography>
                ) : (isErrorEmployees || isErrorClockings || isErrorBreaks) ? (
                    <Typography sx={{textAlign: 'center', mt: 5, color: 'red'}}>Error: Could not load data.</Typography>
                ) : (processedData && processedData.length > 0) ? (
                    <DataGrid
                        loading={isLoadingEmployees || isLoadingClockings || isLoadingBreaks}
                        rows={processedData || []}
                        columns={columns}
                        //slots={{ toolbar: GridToolbar }}
                        rowHeight={isMobileScreen ? 80 : 100}
                    />
                ) : (
                    <Typography sx={{textAlign: 'center', mt: 5}}>
                        {selectedFilter === "All" 
                            ? "No employees found." 
                            : `No employees on "${selectedFilter}".`
                        }
                    </Typography>
                )}
            </Box>

            {/* Reset PIN Dialog */}
            <Dialog open={openResetPinDialog} onClose={() => setOpenResetPinDialog(false)}>
                <DialogTitle >RESET PIN FOR {selectedRow?.names.firstName}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="New PIN"
                        type="password"
                        fullWidth
                        variant="standard"
                        value={newPin}
                        onChange={(e) => setNewPin(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenResetPinDialog(false)}
                            variant="contained"
                            color="secondary"
                            sx={{
                                backgroundColor: theme.palette.error.main,
                            }}
                        >
                            Cancel
                        </Button>
                    <Button
                        onClick={handleResetPinSubmit}
                            variant="contained"
                            color="secondary"
                            sx={{
                                backgroundColor: theme.palette.secondary.main,
                            }}
                        >
                            Submit
                        </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
            >
                <DialogTitle>CONFIRM DELETION</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete {selectedRow?.displayName}? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDelete}
                            variant="contained"
                            color="secondary"
                            sx={{
                                backgroundColor: theme.palette.error.main,
                            }}
                        >
                            Delete
                    </Button>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                            variant="contained"
                            color="secondary"
                            sx={{
                                backgroundColor: theme.palette.secondary.main,
                            }}
                        >
                            Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Dialog */}
            <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ textTransform: 'uppercase' }}>EMPLOYEE DETAILS</DialogTitle>
                <DialogContent>
                    <Grid container spacing={1}>
                        {selectedRow && Object.entries(selectedRow).map(([key, value]) => {
                            if (typeof value !== 'object' || value === null) {
                                // Format date fields properly
                                let displayValue = String(value);
                                
                                if (['DoB', 'hireDate', 'createdAt', 'updatedAt'].includes(key) && value) {
                                    displayValue = moment(value).format("DD/MM/YYYY");
                                } else if (key === 'phone' && value) {
                                    // Format phone number
                                    displayValue = value.replace(/^(\d{3})(\d{3})(\d{4})/, "($1)$2-$3");
                                } else if (key === 'status' && value) {
                                    // Capitalize status
                                    displayValue = value.charAt(0).toUpperCase() + value.slice(1);
                                } else if (key === 'employmentType' && value) {
                                    // Format employment type
                                    displayValue = value.split('-').map(word => 
                                        word.charAt(0).toUpperCase() + word.slice(1)
                                    ).join(' ');
                                }
                                
                                // Skip internal fields that shouldn't be displayed
                                if (['_id', 'id', '__v', 'pin', 'shifts', 'displayName', 'displayShiftTime'].includes(key)) {
                                    return null;
                                }
                                
                                // Format field names for display
                                let displayKey = key;
                                if (key === 'DoB') displayKey = 'Date of Birth';
                                else if (key === 'hireDate') displayKey = 'Hire Date';
                                else if (key === 'employmentType') displayKey = 'Employment Type';
                                else if (key === 'createdAt') displayKey = 'Created At';
                                else if (key === 'updatedAt') displayKey = 'Updated At';
                                else {
                                    // Capitalize first letter for other fields
                                    displayKey = key.charAt(0).toUpperCase() + key.slice(1);
                                }

                                return (
                                    <Grid item xs={12} sm={12} md={12} key={key}>
                                        <Card variant="outlined" sx={{ py: .2, px: 1.5 }}>
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    textTransform: 'uppercase', 
                                                    color: 'text.secondary', 
                                                    fontWeight: 'bold' 
                                                }}
                                            >
                                                {displayKey}
                                            </Typography>
                                            <Typography variant="h6">
                                                {displayValue || 'N/A'}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                );
                            } else if (key === 'names' && value) {
                                // Handle names object specially
                                return (
                                    <Grid item xs={12} sm={12} md={12} key={key}>
                                        <Card variant="outlined" sx={{ py: .2, px: 1.5 }}>
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    textTransform: 'uppercase', 
                                                    color: 'text.secondary', 
                                                    fontWeight: 'bold' 
                                                }}
                                            >
                                                Full Name
                                            </Typography>
                                            <Typography variant="h6">
                                                {`${value.firstName || ''} ${value.middleName || ''} ${value.lastName || ''}`.trim()}
                                            </Typography>
                                            {value.preferredName && (
                                                <>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            textTransform: 'uppercase', 
                                                            color: 'text.secondary', 
                                                            fontWeight: 'bold',
                                                            mt: 1,
                                                            display: 'block'
                                                        }}
                                                    >
                                                        Preferred Name
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {value.preferredName}
                                                    </Typography>
                                                </>
                                            )}
                                        </Card>
                                    </Grid>
                                );
                            }
                            return null;
                        })}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenViewDialog(false)}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>EDIT EMPLOYEE</DialogTitle>
                <DialogContent>
                    {editableEmployeeData && Object.entries(editableEmployeeData).map(([key, value]) => {
                        if (key === 'names') {
                            return Object.entries(value).map(([nameKey, nameValue]) => (
                                <TextField
                                    key={nameKey}
                                    margin="dense"
                                    label={nameKey}
                                    type="text"
                                    fullWidth
                                    variant="standard"
                                    name={nameKey}
                                    value={nameValue}
                                    onChange={handleInputChange}
                                />
                            ));
                        }
                        if (['email', 'phone', 'department', 'role', 'status', 'employmentType', 'DoB'].includes(key)) {
                            return (
                                <TextField
                                    key={key}
                                    margin="dense"
                                    label={key}
                                    type={key === 'DoB' ? 'date' : 'text'}
                                    fullWidth
                                    variant="standard"
                                    name={key}
                                    value={value || ''}
                                    onChange={handleInputChange}
                                    InputLabelProps={key === 'DoB' ? { shrink: true } : {}}
                                />
                            );
                        }
                        return null;
                    })}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenEditDialog(false)}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.error.main,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEditSubmit}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Employee Dialog */}
            <Dialog open={openAddDialog} onClose={handleAddDialogClose} fullWidth maxWidth="sm">
                <DialogTitle>ADD NEW EMPLOYEE</DialogTitle>
                <DialogContent>
                    {/* First Name */}
                    <TextField autoFocus margin="dense" label="First Name*" name="firstName" value={newEmployeeData.names.firstName} onChange={handleNewEmployeeInputChange} fullWidth variant="standard" />
                    {/* Last Name */}
                    <TextField margin="dense" label="Last Name*" name="lastName" value={newEmployeeData.names.lastName} onChange={handleNewEmployeeInputChange} fullWidth variant="standard" />
                    {/* Middle Name */}
                    <TextField margin="dense" label="Middle Name" name="middleName" value={newEmployeeData.names.middleName} onChange={handleNewEmployeeInputChange} fullWidth variant="standard" />
                    {/* Preferred Name */}
                    <TextField margin="dense" label="Preferred Name" name="preferredName" value={newEmployeeData.names.preferredName} onChange={handleNewEmployeeInputChange} fullWidth variant="standard" />
                    {/* Email */}
                    <TextField margin="dense" label="Email*" name="email" type="email" value={newEmployeeData.email} onChange={handleNewEmployeeInputChange} fullWidth variant="standard" />
                    {/* Phone */}
                    <TextField margin="dense" label="Phone" name="phone" value={newEmployeeData.phone} onChange={handleNewEmployeeInputChange} fullWidth variant="standard" />
                    {/* Date of Birth */}
                    <TextField margin="dense" label="Date of Birth" name="DoB" type="date" value={newEmployeeData.DoB} onChange={handleNewEmployeeInputChange} fullWidth variant="standard" InputLabelProps={{ shrink: true }} />
                    {/* PIN */}
                    <TextField margin="dense" label="PIN*" name="pin" type="password" value={newEmployeeData.pin} onChange={handleNewEmployeeInputChange} fullWidth variant="standard" />
                    {/* Department */}
                    <TextField margin="dense" label="Department" name="department" value={newEmployeeData.department} onChange={handleNewEmployeeInputChange} fullWidth variant="standard" />
                    {/* Employment Type */}
                    <TextField margin="dense" label="Employment Type" name="employmentType" value={newEmployeeData.employmentType} onChange={handleNewEmployeeInputChange} fullWidth variant="standard" />
                     {/* Hire Date */}
                     <TextField margin="dense" label="Hire Date" name="hireDate" type="date" value={newEmployeeData.hireDate} onChange={handleNewEmployeeInputChange} fullWidth variant="standard" InputLabelProps={{ shrink: true }} />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleAddDialogClose}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.error.main,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddSubmit}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Add Employee
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Users;
